import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormHelperText,
  IconButton,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Group as GroupIcon,
} from "@mui/icons-material";
import DeleteDialog from "src/reusable/components/DeleteDialog";
import RetryableError from "src/views/assistant/projects/components/RetryableError";
import GroupMemberPicker, {
  type SearchUser,
} from "src/views/admin/groups/GroupMemberPicker";
import { useGroups } from "src/views/admin/groups/groupService";
import {
  useAddResourceGroupShare,
  useAddResourceUserShare,
  useApplyShareGrants,
  useRemoveResourceGroupShare,
  useRemoveResourceUserShare,
  useResourceShareCoverage,
  useResourceShares,
  useSetResourceVisibility,
  useTransferResourceOwner,
  type ShareLevel,
  type ShareResourceType,
  type ShareVisibility,
} from "src/api/shares";
import AccessList, {
  type DatasetShareTarget,
  type PendingRevoke,
} from "./AccessList";
import CoverageDatasetsBlock, {
  type DatasetShareProgress,
} from "./CoverageDatasetsBlock";
import PermissionSelect, {
  labelForLevel,
  normalizeShareLevel,
} from "./PermissionSelect";
import TransferOwnershipSection from "./TransferOwnershipSection";
import VisibilityControl from "./VisibilityControl";
import { SHARE_DIALOG_COPY } from "./copy";

export interface ShareDialogResource {
  type: ShareResourceType;
  id: number;
  name: string;
}

export interface ShareDialogProps {
  open: boolean;
  onClose: () => void;
  resource: ShareDialogResource;
  /** Show the "Transfer ownership" control (owner or admin only). */
  canTransferOwnership?: boolean;
  /** Whether the caller's role may set the resource public. When false the
   *  public option is withheld with a note; a resource that is already
   *  public still shows that state. Agents key this on agent:promote. */
  canPublish?: boolean;
}

// Types whose grants reach attached datasets the grant itself does not
// cover, so the dialog computes and shows per-recipient coverage.
const COVERAGE_TYPES = new Set<ShareResourceType>(["agent"]);

const publicCaptionWithPrivateDatasets = (
  base: string,
  privateCount: number,
  total: number,
) =>
  privateCount === 0
    ? base
    : `${base} ${privateCount} of its ${total} ${total === 1 ? "dataset is" : "datasets are"} private, so people without access to those see the agent with reduced access or not at all.`;

// Testids keep the legacy per-type prefixes the dataset and folder tests pin.
const TEST_ID_PREFIX: Record<ShareResourceType, string> = {
  dataset: "share-dataset",
  datasetFolder: "share-folder",
  agent: "share-agent",
};

const PUBLISH_WITHHELD_NOTE =
  "Publishing to the whole organization is a privilege on the role. Your " +
  "role does not hold it, so that option is not offered here; an " +
  "administrator can publish this agent for you.";

const apiErrorDetail = (err: unknown, fallback: string): string =>
  axios.isAxiosError(err) && typeof err.response?.data?.detail === "string"
    ? err.response.data.detail
    : fallback;

// manage and owner are the same rank — two domains' labels for the one
// admin tier (MRE #30597) — so neither outranks nor demotes the other.
const LEVEL_RANK: Record<ShareLevel, number> = {
  read: 0,
  write: 1,
  manage: 2,
  owner: 2,
};

interface InheritedAccess {
  level: ShareLevel;
  folderName: string;
}

/**
 * The strongest folder-derived access a subject already holds, or null when it
 * has none. A subject can appear in the inherited rows more than once (a grant
 * on the folder plus owning that folder), and its effective access is the
 * highest of them — which is also what a direct grant would cap.
 */
const inheritedAccessFor = <
  T extends { permission_level: ShareLevel; source_folder_name: string },
>(
  rows: T[] | undefined,
  matches: (row: T) => boolean,
): InheritedAccess | null =>
  (rows ?? []).reduce<InheritedAccess | null>(
    (best, row) =>
      !matches(row) ||
      (best && LEVEL_RANK[best.level] >= LEVEL_RANK[row.permission_level])
        ? best
        : { level: row.permission_level, folderName: row.source_folder_name },
    null,
  );

/**
 * Reusable share dialog for any System-B share-aware resource (visibility +
 * per-user/per-group grants + owner transfer). Resource-type differences
 * (routes, copy) are config-driven — see src/api/shares and
 * ./copy.ts. Testids are prefixed per type (see TEST_ID_PREFIX).
 */
const ShareDialog: React.FC<ShareDialogProps> = ({
  open,
  onClose,
  resource,
  canTransferOwnership = false,
  canPublish = true,
}) => {
  const copy = SHARE_DIALOG_COPY[resource.type];
  const tid = TEST_ID_PREFIX[resource.type];

  const [activeTab, setActiveTab] = useState(0);
  const [selectedUser, setSelectedUser] = useState<SearchUser | null>(null);
  const [userPermission, setUserPermission] = useState<ShareLevel>("read");
  const [selectedGroup, setSelectedGroup] = useState<{
    id: number;
    name: string;
  } | null>(null);
  const [groupSearchQuery, setGroupSearchQuery] = useState("");
  const [groupPermission, setGroupPermission] = useState<ShareLevel>("read");
  const [pendingRevoke, setPendingRevoke] = useState<PendingRevoke | null>(
    null,
  );
  const [transferUser, setTransferUser] = useState<SearchUser | null>(null);
  const [pendingTransfer, setPendingTransfer] = useState<{
    id: number;
    label: string;
  } | null>(null);

  const {
    data: shares,
    error: sharesError,
    isLoading: sharesLoading,
    refetch: refetchShares,
  } = useResourceShares(resource.type, open ? resource.id : null, open);
  const {
    data: groupsData,
    error: groupsError,
    refetch: refetchGroups,
  } = useGroups();

  const addUser = useAddResourceUserShare(resource.type);
  const addGroup = useAddResourceGroupShare(resource.type);
  const removeUser = useRemoveResourceUserShare(resource.type);
  const removeGroup = useRemoveResourceGroupShare(resource.type);
  const setVisibility = useSetResourceVisibility(resource.type);
  const transferOwner = useTransferResourceOwner(resource.type);

  // Per-recipient dataset coverage, recomputed on every open and after every
  // share change (the resource's invalidate keys include it). Adding a
  // recipient never waits on it: the chip appears once it lands.
  const showCoverage = COVERAGE_TYPES.has(resource.type);
  const {
    data: coverageData,
    error: coverageError,
    isLoading: coverageLoading,
    refetch: refetchCoverage,
  } = useResourceShareCoverage(
    resource.type,
    open && showCoverage ? resource.id : null,
    open && showCoverage,
  );
  // Offers grant View only on the DATASET, so they go through the dataset
  // share routes, then recompute coverage for this dialog. The bulk offer
  // applies its grants as one batch so the dataset lists refetch once.
  const addDatasetUser = useAddResourceUserShare("dataset");
  const addDatasetGroup = useAddResourceGroupShare("dataset");
  const applyDatasetGrants = useApplyShareGrants("dataset");
  const [datasetShareProgress, setDatasetShareProgress] =
    useState<DatasetShareProgress | null>(null);
  const [datasetShareKey, setDatasetShareKey] = useState<string | null>(null);
  const [datasetShareError, setDatasetShareError] = useState<string | null>(
    null,
  );
  // One dataset grant at a time, bulk or inline: both paths disable on this.
  const datasetShareBusy =
    datasetShareProgress !== null || datasetShareKey !== null;

  const shareDatasetWithMissing = async (
    datasetId: number,
    userIds: number[],
  ) => {
    if (datasetShareBusy) return;
    setDatasetShareError(null);
    const total = userIds.length;
    setDatasetShareProgress({ datasetId, done: 0, total });
    const { failed } = await applyDatasetGrants(
      datasetId,
      userIds.map((id) => ({
        kind: "user" as const,
        id,
        level: "read" as const,
      })),
      (done) => setDatasetShareProgress({ datasetId, done, total }),
    );
    setDatasetShareProgress(null);
    if (failed.length > 0) {
      setDatasetShareError(
        `Couldn't share the dataset with ${failed.length} of ${total}. Try again.`,
      );
    }
    void refetchCoverage();
  };

  const shareDatasetWith = async (target: DatasetShareTarget) => {
    if (datasetShareBusy) return;
    setDatasetShareError(null);
    setDatasetShareKey(`${target.kind}-${target.id}-${target.datasetId}`);
    try {
      if (target.kind === "user") {
        await addDatasetUser.mutateAsync({
          resourceId: target.datasetId,
          data: { user_id: target.id, permission_level: "read" },
        });
      } else {
        await addDatasetGroup.mutateAsync({
          resourceId: target.datasetId,
          data: { group_id: target.id, permission_level: "read" },
        });
      }
    } catch (err) {
      setDatasetShareError(
        apiErrorDetail(err, "Couldn't share the dataset. Try again."),
      );
    } finally {
      setDatasetShareKey(null);
    }
    void refetchCoverage();
  };

  // Add waits on the same payload the inherited-access seed and warnings
  // read (existingUserIds/existingGroupIds too) — adding before it arrives,
  // or after it failed to arrive at all, risks a duplicate grant the
  // backend 400s or a demotion of a folder-inherited subject with no
  // warning shown.
  const sharesUnavailable = sharesLoading || sharesError != null;

  // Local (optimistic) visibility so the radio responds immediately on click;
  // synced from the server whenever the shares query (re)loads. The backend
  // flips public->private itself when a share is added, so no client-side
  // auto-flip exists — the refetch after a share mutation brings it in.
  const [visibility, setVisibilityLocal] = useState<ShareVisibility>("public");
  useEffect(() => {
    if (shares) {
      setVisibilityLocal(
        shares.visibility_level === "public" ? "public" : "private",
      );
    }
  }, [shares]);

  const handleVisibilityChange = (next: ShareVisibility) => {
    setVisibilityLocal(next); // optimistic
    setVisibility.mutate(
      { resourceId: resource.id, visibility: next },
      {
        // Revert the optimistic flip on failure so the control reflects
        // reality; setVisibility.isError above is what tells the user why
        // it snapped back instead of leaving them to wonder.
        onError: () =>
          setVisibilityLocal(
            shares?.visibility_level === "public" ? "public" : "private",
          ),
      },
    );
  };

  const existingUserIds = useMemo(
    () => shares?.user_shares.map((s) => s.user_id) || [],
    [shares],
  );
  const existingGroupIds = useMemo(
    () => new Set(shares?.group_shares.map((s) => s.group_id) || []),
    [shares],
  );
  const availableGroups = useMemo(
    () => (groupsData?.items || []).filter((g) => !existingGroupIds.has(g.id)),
    [groupsData, existingGroupIds],
  );

  // Subjects with folder-derived access stay pickable on purpose (a direct
  // grant is how you raise them above the folder level), but the pickers have
  // to say what that grant does. Only types whose copy describes inherited
  // access take part — the same guard AccessList applies before rendering
  // inherited rows, so the pickers never warn about rows the list hides.
  const inheritedCopy = copy.inheritedAccess;
  const inheritedUsers = inheritedCopy
    ? shares?.inherited_user_shares
    : undefined;
  const inheritedGroups = inheritedCopy
    ? shares?.inherited_group_shares
    : undefined;
  const inheritedRoles = inheritedCopy
    ? shares?.inherited_role_shares
    : undefined;

  // Seeded at selection time, not derived on every render, so a manual change
  // to the select afterwards stands. Seeds are normalized: an inherited
  // "owner" must land on the select as "manage", the only label offered.
  const handleSelectUser = (user: SearchUser | null) => {
    setSelectedUser(user);
    const inherited = user
      ? inheritedAccessFor(inheritedUsers, (r) => r.user_id === user.id)
      : null;
    setUserPermission(
      inherited ? normalizeShareLevel(inherited.level ?? "read") : "read",
    );
  };

  const handleSelectGroup = (group: { id: number; name: string } | null) => {
    setSelectedGroup(group);
    const inherited = group
      ? inheritedAccessFor(inheritedGroups, (r) => r.group_id === group.id)
      : null;
    setGroupPermission(
      inherited ? normalizeShareLevel(inherited.level ?? "read") : "read",
    );
  };

  const userInheritedWarning = useMemo(() => {
    if (!inheritedCopy || !selectedUser) return null;
    const inherited = inheritedAccessFor(
      inheritedUsers,
      (r) => r.user_id === selectedUser.id,
    );
    return (
      inherited &&
      inheritedCopy.directGrantWarning(
        selectedUser.user_display_name || selectedUser.username,
        labelForLevel(inherited.level),
        inherited.folderName,
      )
    );
  }, [inheritedCopy, inheritedUsers, selectedUser]);

  // A direct USER grant caps the whole identity, so it also caps access the
  // person gets through a group or role granted on the folder — membership
  // the client cannot see. When the picked user has no inherited row of
  // their own but such rows exist, say so, softly.
  const userIndirectWarning = useMemo(() => {
    if (!inheritedCopy || !selectedUser) return null;
    if (
      inheritedAccessFor(inheritedUsers, (r) => r.user_id === selectedUser.id)
    ) {
      return null; // the direct warning above already covers them
    }
    const viaMembership = [
      ...(inheritedGroups ?? []),
      ...(inheritedRoles ?? []),
    ];
    if (viaMembership.length === 0) return null;
    return inheritedCopy.indirectGrantWarning(
      selectedUser.user_display_name || selectedUser.username,
      viaMembership[0].source_folder_name,
    );
  }, [
    inheritedCopy,
    inheritedUsers,
    inheritedGroups,
    inheritedRoles,
    selectedUser,
  ]);

  const groupInheritedWarning = useMemo(() => {
    if (!inheritedCopy || !selectedGroup) return null;
    const inherited = inheritedAccessFor(
      inheritedGroups,
      (r) => r.group_id === selectedGroup.id,
    );
    return (
      inherited &&
      inheritedCopy.directGrantWarning(
        selectedGroup.name,
        labelForLevel(inherited.level),
        inherited.folderName,
      )
    );
  }, [inheritedCopy, inheritedGroups, selectedGroup]);

  const handleAddUser = () => {
    if (!selectedUser) return;
    addUser.mutate(
      {
        resourceId: resource.id,
        data: { user_id: selectedUser.id, permission_level: userPermission },
      },
      {
        onSuccess: () => {
          setSelectedUser(null);
          setUserPermission("read");
        },
      },
    );
  };

  const handleAddGroup = () => {
    if (!selectedGroup) return;
    addGroup.mutate(
      {
        resourceId: resource.id,
        data: { group_id: selectedGroup.id, permission_level: groupPermission },
      },
      {
        onSuccess: () => {
          setSelectedGroup(null);
          setGroupSearchQuery("");
          setGroupPermission("read");
        },
      },
    );
  };

  const confirmRevoke = () => {
    if (!pendingRevoke) return;
    const opts = { onSettled: () => setPendingRevoke(null) };
    if (pendingRevoke.kind === "user") {
      removeUser.mutate(
        { resourceId: resource.id, userId: pendingRevoke.id },
        opts,
      );
    } else {
      removeGroup.mutate(
        { resourceId: resource.id, groupId: pendingRevoke.id },
        opts,
      );
    }
  };

  const confirmTransfer = () => {
    if (!pendingTransfer) return;
    transferOwner.mutate(
      { resourceId: resource.id, newOwnerUserId: pendingTransfer.id },
      {
        onSuccess: () => setTransferUser(null),
        onSettled: () => setPendingTransfer(null),
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid={`${tid}-dialog`}
    >
      <DialogTitle>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
        >
          <Typography variant="h6" fontWeight="bold">
            Share &quot;{resource.name}&quot;
          </Typography>
          <IconButton onClick={onClose} size="small" aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent dividers>
        {(addUser.isError || addGroup.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiErrorDetail(
              addUser.error || addGroup.error,
              "Failed to add share. Please try again.",
            )}
          </Alert>
        )}
        {(removeUser.isError || removeGroup.isError) && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiErrorDetail(
              removeUser.error || removeGroup.error,
              "Failed to revoke access. Please try again.",
            )}
          </Alert>
        )}
        {setVisibility.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {apiErrorDetail(
              setVisibility.error,
              "Failed to update visibility. Please try again.",
            )}
          </Alert>
        )}

        {datasetShareError && (
          <Alert
            severity="error"
            sx={{ mb: 2 }}
            data-testid={`${tid}-dataset-share-error`}
          >
            {datasetShareError}
          </Alert>
        )}

        <VisibilityControl
          visibility={visibility}
          onChange={handleVisibilityChange}
          disabled={setVisibility.isLoading || sharesUnavailable}
          tid={tid}
          publicCaption={
            coverageData
              ? publicCaptionWithPrivateDatasets(
                  copy.publicCaption,
                  coverageData.datasets_private_count,
                  coverageData.datasets_total,
                )
              : copy.publicCaption
          }
          privateCaption={copy.privateCaption}
          publicOptionHidden={!canPublish}
          hiddenNote={PUBLISH_WITHHELD_NOTE}
          visibilityKnown={shares != null}
        />
        <Divider sx={{ mb: 2 }} />

        {showCoverage && (
          <>
            <CoverageDatasetsBlock
              coverage={coverageData}
              isLoading={coverageLoading}
              error={coverageError}
              onRetry={() => void refetchCoverage()}
              tid={tid}
              onShareWithMissing={(datasetId, userIds) =>
                void shareDatasetWithMissing(datasetId, userIds)
              }
              progress={datasetShareProgress}
              busy={datasetShareBusy}
            />
            <Divider sx={{ mb: 2 }} />
          </>
        )}

        <Tabs
          value={activeTab}
          onChange={(_e, v) => setActiveTab(v)}
          sx={{ borderBottom: 1, borderColor: "divider", mb: 2 }}
        >
          <Tab
            data-testid={`${tid}-tab-people`}
            icon={<PersonIcon fontSize="small" />}
            iconPosition="start"
            label="People"
          />
          <Tab
            data-testid={`${tid}-tab-groups`}
            icon={<GroupIcon fontSize="small" />}
            iconPosition="start"
            label="Groups"
          />
        </Tabs>

        {activeTab === 0 && (
          <Box sx={{ mb: 3 }}>
            <Stack spacing={2}>
              <GroupMemberPicker
                open={open}
                excludeIds={existingUserIds}
                value={selectedUser}
                onChange={handleSelectUser}
                label="Search users by name or username"
                textFieldTestId={`${tid}-user-search`}
              />
              {userInheritedWarning && (
                <Alert
                  severity="warning"
                  data-testid={`${tid}-user-inherited-warning`}
                >
                  {userInheritedWarning}
                </Alert>
              )}
              {userIndirectWarning && (
                <Alert
                  severity="info"
                  data-testid={`${tid}-user-indirect-warning`}
                >
                  {userIndirectWarning}
                </Alert>
              )}
              <Stack direction="row" spacing={2} alignItems="center">
                <PermissionSelect
                  value={userPermission}
                  onChange={setUserPermission}
                  testId={`${tid}-user-permission`}
                />
                {/* Waits for the shares payload: the seed and the warnings
                    read it, so adding before it arrives could demote a
                    folder-inherited subject with no warning shown. */}
                <Button
                  variant="contained"
                  size="small"
                  onClick={handleAddUser}
                  disabled={
                    !selectedUser || addUser.isLoading || sharesUnavailable
                  }
                  data-testid={`${tid}-user-add-btn`}
                >
                  {addUser.isLoading ? <CircularProgress size={20} /> : "Add"}
                </Button>
              </Stack>
            </Stack>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ mb: 3 }}>
            {groupsError ? (
              <RetryableError
                message="Couldn't load groups."
                onRetry={() => void refetchGroups()}
                testId={`${tid}-groups-error`}
              />
            ) : (
              <Stack spacing={2}>
                <Autocomplete
                  options={availableGroups}
                  getOptionLabel={(o) => o.name}
                  // By id, not identity: the options array is rebuilt
                  // whenever the groups query re-renders, which would
                  // otherwise orphan the selection MUI is holding.
                  isOptionEqualToValue={(o, v) => o.id === v.id}
                  value={selectedGroup}
                  onChange={(_e, value) => handleSelectGroup(value)}
                  inputValue={groupSearchQuery}
                  onInputChange={(_e, value) => setGroupSearchQuery(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Search groups by name"
                      size="small"
                      data-testid={`${tid}-group-search`}
                    />
                  )}
                />
                {groupInheritedWarning && (
                  <Alert
                    severity="warning"
                    data-testid={`${tid}-group-inherited-warning`}
                  >
                    {groupInheritedWarning}
                  </Alert>
                )}
                <Stack direction="row" spacing={2} alignItems="center">
                  <PermissionSelect
                    value={groupPermission}
                    onChange={setGroupPermission}
                    testId={`${tid}-group-permission`}
                  />
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleAddGroup}
                    disabled={
                      !selectedGroup || addGroup.isLoading || sharesUnavailable
                    }
                    data-testid={`${tid}-group-add-btn`}
                  >
                    {addGroup.isLoading ? (
                      <CircularProgress size={20} />
                    ) : (
                      "Add"
                    )}
                  </Button>
                </Stack>
                <FormHelperText>{copy.groupTabHelper}</FormHelperText>
              </Stack>
            )}
          </Box>
        )}

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          People &amp; groups with access
        </Typography>

        <AccessList
          shares={shares}
          isLoading={sharesLoading}
          error={sharesError}
          onRetry={() => void refetchShares()}
          tid={tid}
          emptyText={copy.emptyAccessText}
          inheritedCopy={copy.inheritedAccess}
          onRevoke={setPendingRevoke}
          revokeDisabled={removeUser.isLoading || removeGroup.isLoading}
          coverage={showCoverage ? coverageData : undefined}
          onShareDataset={(target) => void shareDatasetWith(target)}
          // A bulk share holds every inline offer too, with no row spinner.
          sharingKey={datasetShareKey ?? (datasetShareProgress ? "bulk" : null)}
        />

        {canTransferOwnership && (
          <TransferOwnershipSection
            open={open}
            tid={tid}
            value={transferUser}
            onChange={setTransferUser}
            onRequestTransfer={() =>
              transferUser &&
              setPendingTransfer({
                id: transferUser.id,
                label: transferUser.user_display_name || transferUser.username,
              })
            }
            isLoading={transferOwner.isLoading}
            errorMessage={
              transferOwner.isError
                ? apiErrorDetail(
                    transferOwner.error,
                    "Failed to transfer ownership. Please try again.",
                  )
                : null
            }
            helperText={copy.transferHelperText}
          />
        )}
      </DialogContent>
      <DeleteDialog
        open={pendingRevoke !== null}
        onClose={() => setPendingRevoke(null)}
        title={
          pendingRevoke
            ? `Revoke access for "${pendingRevoke.label}"?`
            : "Revoke access?"
        }
        content={
          pendingRevoke?.kind === "group"
            ? copy.revokeGroupContent
            : copy.revokeUserContent
        }
        onDelete={confirmRevoke}
        isLoading={removeUser.isLoading || removeGroup.isLoading}
        confirmButtonText="Revoke access"
        tid={`${tid}-revoke`}
      />
      <DeleteDialog
        open={pendingTransfer !== null}
        onClose={() => setPendingTransfer(null)}
        title={
          pendingTransfer
            ? `Transfer ownership to "${pendingTransfer.label}"?`
            : "Transfer ownership?"
        }
        content={copy.transferConfirmContent}
        onDelete={confirmTransfer}
        isLoading={transferOwner.isLoading}
        confirmButtonText="Transfer ownership"
        tid={`${tid}-transfer`}
      />
    </Dialog>
  );
};

export default ShareDialog;
